import React, { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { TabNav } from '../../components/ui/TabNav.js';
import {
  CheckCircle, AlertCircle, ExternalLink, Unlink,
  Bell, Calendar, Shield, Building2, FileText,
  Link2, Copy, Check, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiDelete } from '../../api/client';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { usePermissions } from '../../hooks/usePermission';
import { Button } from '../../components/ui/Button';
import { Section } from '../../components/ui/Section';
import StudioProfileSection from './sections/StudioProfileSection';
import NtfyNotificationsSection from './sections/NtfyNotificationsSection';
import CalendarSettingsSection from './sections/CalendarSettingsSection';
import AvailabilitySection from './sections/AvailabilitySection';
import BackupSection from './sections/BackupSection';
import SystemHealthSection from './sections/SystemHealthSection';
import GcalSyncLogSection from './sections/GcalSyncLogSection';
import AdminActionsSection from './sections/AdminActionsSection';
import SmtpConfigSection from './sections/SmtpConfigSection';
import GoogleConfigSection from './sections/GoogleConfigSection';
import InfoAppSection from './sections/InfoAppSection';
import CalendarMaintenanceSection from './sections/CalendarMaintenanceSection';
import ImportExportSection from './ImportExportSection';
import { LegalDocumentsSection } from './LegalDocumentsSection';
import { GoogleCalendarSelector } from '../../components/settings/GoogleCalendarSelector';

interface MeExtended { id: string; email: string; name: string; role: string; hasGoogleCalendar: boolean; hasPersonalGoogleCalendar: boolean; personalCalendarToken?: string | null; avatarUrl?: string | null; googleCalendarId?: string | null; personalGoogleCalendarId?: string | null; }

type Tab = 'studio' | 'notifications' | 'calendar' | 'system' | 'documents';

type PermKey = 'settings.studio.view' | 'settings.notifications.view' | 'settings.calendar.view' | 'settings.documents.view' | 'settings.system.view';

const TABS: { key: Tab; label: string; icon: React.ReactNode; permKey: PermKey }[] = [
  { key: 'studio',        label: 'Profilo Studio',   icon: <Building2 size={15} />,  permKey: 'settings.studio.view' },
  { key: 'notifications', label: 'Notifiche',         icon: <Bell size={15} />,       permKey: 'settings.notifications.view' },
  { key: 'calendar',      label: 'Calendario',        icon: <Calendar size={15} />,   permKey: 'settings.calendar.view' },
  { key: 'documents',     label: 'Documenti Legali',  icon: <FileText size={15} />,   permKey: 'settings.documents.view' },
  { key: 'system',        label: 'Sistema',           icon: <Shield size={15} />,     permKey: 'settings.system.view' },
];

const TAB_PARAM_MAP: Record<string, Tab> = {
  studio: 'studio', notifiche: 'notifications', calendario: 'calendar', documenti: 'documents', sistema: 'system',
};
const TAB_TO_PARAM: Record<Tab, string> = {
  studio: 'studio', notifications: 'notifiche', calendar: 'calendario', documents: 'documenti', system: 'sistema',
};

export default function SettingsPage() {
  const qc = useQueryClient();
  const isAdmin = useIsAdmin();
  const { hasPermission } = usePermissions();
  const canStudioView    = hasPermission('settings.studio.view');
  const canNotifsView    = hasPermission('settings.notifications.view');
  const canCalendarView  = hasPermission('settings.calendar.view');
  const canDocsView      = hasPermission('settings.documents.view');
  const canSystemView    = hasPermission('settings.system.view');

  const [searchParams, setSearchParams] = useSearchParams();

  const tabPermMap: Record<Tab, boolean> = {
    studio:        canStudioView,
    notifications: canNotifsView,
    calendar:      canCalendarView,
    documents:     canDocsView,
    system:        canSystemView,
  };

  const visibleTabs = TABS.filter(t => tabPermMap[t.key] ?? false);

  const initialTab = TAB_PARAM_MAP[searchParams.get('tab') ?? ''] ?? 'studio';
  const safeInitialTab = visibleTabs.find(t => t.key === initialTab)?.key ?? visibleTabs[0]?.key ?? 'studio';
  const [activeTab, setActiveTab] = useState<Tab>(safeInitialTab);

  useEffect(() => {
    const paramTab = TAB_PARAM_MAP[searchParams.get('tab') ?? ''];
    if (paramTab && paramTab !== activeTab) setActiveTab(paramTab);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTab = useCallback((tab: Tab) => {
    setActiveTab(tab);
    setSearchParams({ tab: TAB_TO_PARAM[tab] }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (searchParams.get('calendarConnected') === '1') {
      toast.success('Google Calendar connesso con successo!');
      qc.invalidateQueries({ queryKey: ['me'] });
      setSearchParams({ tab: TAB_TO_PARAM.calendar }, { replace: true });
      setActiveTab('calendar');
    } else if (searchParams.get('calendarError') === '1') {
      toast.error('Errore durante la connessione a Google Calendar.');
      setSearchParams({ tab: TAB_TO_PARAM[activeTab] }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some(t => t.key === activeTab)) {
      switchTab(visibleTabs[0].key);
    }
  }, [visibleTabs.map(t => t.key).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: me } = useQuery<MeExtended>({
    queryKey: ['me'],
    queryFn: () => apiGet<MeExtended>('/auth/me'),
  });

  const calAuthMutation = useMutation({
    mutationFn: () => apiGet<{ url: string }>('/calendar/auth-url-with-state'),
    onSuccess: (data) => { window.location.href = (data as { url: string }).url; },
    onError: (e: Error) => toast.error(e.message),
  });

  const calDisconnectMutation = useMutation({
    mutationFn: () => apiDelete('/calendar/disconnect'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['me'] }); toast.success('Google Calendar disconnesso'); },
    onError: (e: Error) => toast.error(e.message),
  });

  interface CalendarTokenData {
    token: string;
    icalUrl: string;
  }

  const [copiedToken, setCopiedToken] = useState(false);

  const { data: calendarToken, refetch: fetchCalendarToken } = useQuery<CalendarTokenData>({
    queryKey: ['calendar-token'],
    queryFn: () => apiGet<CalendarTokenData>('/auth/calendar-token'),
    enabled: false,
  });

  const regenerateCalendarToken = useMutation({
    mutationFn: () => apiDelete<CalendarTokenData>('/auth/calendar-token'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['calendar-token'] });
      fetchCalendarToken();
      toast.success('Link calendario rigenerato');
    },
  });

  const personalCalDisconnectMutation = useMutation({
    mutationFn: () => apiDelete('/calendar/personal/disconnect'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['me'] }); toast.success('Google Calendar personale disconnesso'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const personalSyncMutation = useMutation({
    mutationFn: () => apiPost<{ synced: number; created: number; updated: number; errors: string[] }>('/calendar/personal/sync'),
    onSuccess: (data) => {
      toast.success(`Sync completata: ${data.synced} eventi (${data.created} nuovi, ${data.updated} aggiornati)`);
      if (data.errors.length > 0) toast.error(`${data.errors.length} errori: ${data.errors.slice(0, 3).join('; ')}`);
    },
    onError: (e: Error) => toast.error(`Sync fallita: ${e.message}`),
  });

  const getIcalUrl = () =>
    calendarToken?.token ? `${window.location.origin}/api/public/calendar/${calendarToken.token}.ics` : '';

  const copyIcalUrl = () => {
    const url = getIcalUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
      toast.success('Link copiato!');
    });
  };

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <PageHeader title="Impostazioni" />
      <TabNav
        tabs={visibleTabs.map(t => ({ id: t.key, label: t.label, icon: t.icon }))}
        active={activeTab}
        onChange={(id) => switchTab(id as Tab)}
      />

      {/* Tab: Profilo Studio */}
      {activeTab === 'studio' && <StudioProfileSection />}

      {/* Tab: Notifiche */}
      {activeTab === 'notifications' && <NtfyNotificationsSection />}

      {/* Tab: Calendario */}
      {activeTab === 'calendar' && (
        <>
          <AvailabilitySection />
          <CalendarSettingsSection isAdmin={isAdmin} />
          <Section title="Google Calendar">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

              {/* ── Calendario dello studio (globale) ── */}
              <div style={{
                padding: '1rem', background: 'var(--glass-bg-card)',
                borderRadius: 'var(--radius-card)', border: '1px solid rgba(255,255,255,0.10)',
                display: 'flex', flexDirection: 'column', gap: '0.75rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Building2 size={15} style={{ color: 'var(--color-primary)' }} />
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>Calendario dello studio</span>
                </div>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', margin: 0 }}>
                  Include tutti gli eventi LensCRM. Visibile a tutto lo studio.
                </p>
                {me?.hasGoogleCalendar ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <CheckCircle size={16} style={{ color: 'var(--color-success)' }} />
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-success)', fontWeight: 500 }}>Connesso</span>
                      </div>
                      <Button variant="danger" size="sm" onClick={() => calDisconnectMutation.mutate()} loading={calDisconnectMutation.isPending} icon={<Unlink size={14} />}>
                        Disconnetti
                      </Button>
                    </div>
                    <GoogleCalendarSelector
                      type="studio"
                      connected={!!me?.hasGoogleCalendar}
                      selectedCalendarId={me?.googleCalendarId}
                      onRefreshNeeded={() => qc.refetchQueries({ queryKey: ['me'] })}
                    />
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <AlertCircle size={16} style={{ color: 'var(--color-text-muted)' }} />
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Non connesso</span>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => calAuthMutation.mutate()} loading={calAuthMutation.isPending} icon={<ExternalLink size={14} />}>
                      Connetti
                    </Button>
                  </div>
                )}
              </div>

              {/* ── Calendario personale ── */}
              <div style={{
                padding: '1rem', background: 'var(--glass-bg-card)',
                borderRadius: 'var(--radius-card)', border: '1px solid rgba(255,255,255,0.10)',
                display: 'flex', flexDirection: 'column', gap: '0.75rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar size={15} style={{ color: 'var(--color-primary)' }} />
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>Il tuo calendario personale</span>
                </div>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', margin: 0 }}>
                  Include solo gli eventi dove sei coinvolto come collaboratore. Ogni utente ha il suo.
                </p>

                {/* iCal subscription */}
                <div style={{
                  padding: '0.75rem', background: 'var(--glass-bg-card)',
                  borderRadius: 'var(--radius-interactive)', border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', flexDirection: 'column', gap: '0.5rem',
                }}>
                  <p style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-text-muted)', margin: 0 }}>
                    Abbonamento iCal
                  </p>
                  {calendarToken ? (
                    <>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.375rem 0.75rem',
                        background: 'var(--glass-bg-card)',
                        borderRadius: 'var(--radius-interactive)',
                        border: '1px solid rgba(255,255,255,0.10)',
                      }}>
                        <span style={{ flex: 1, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {getIcalUrl()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <Button size="sm" variant="secondary" icon={copiedToken ? <Check size={13} /> : <Copy size={13} />} onClick={copyIcalUrl}>
                          {copiedToken ? 'Copiato' : 'Copia link'}
                        </Button>
                        <Button size="sm" variant="secondary" icon={<ExternalLink size={13} />}
                          onClick={() => window.open(getIcalUrl(), '_blank')}>
                          Apri
                        </Button>
                        <Button size="sm" variant="ghost" icon={<RefreshCw size={13} />}
                          loading={regenerateCalendarToken.isPending}
                          onClick={() => {
                            if (confirm('Rigenerare il link? Il vecchio non funzionera piu.'))
                              regenerateCalendarToken.mutate();
                          }}>
                          Rigenera
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Button size="sm" variant="secondary" icon={<Link2 size={13} />}
                      onClick={() => fetchCalendarToken()}>
                      Genera link abbonamento
                    </Button>
                  )}
                </div>

                {/* Google Calendar OAuth personale */}
                <div style={{
                  padding: '0.75rem', background: 'var(--glass-bg-card)',
                  borderRadius: 'var(--radius-interactive)', border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', flexDirection: 'column', gap: '0.5rem',
                }}>
                  <p style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-text-muted)', margin: 0 }}>
                    Sincronizzazione Google Calendar
                  </p>
                  {me?.hasPersonalGoogleCalendar ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <CheckCircle size={16} style={{ color: 'var(--color-success)' }} />
                          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-success)', fontWeight: 500 }}>Connesso</span>
                        </div>
                        <Button size="sm" variant="ghost"
                          onClick={() => personalCalDisconnectMutation.mutate()}>
                          Disconnetti
                        </Button>
                      </div>
                      <GoogleCalendarSelector
                        type="personal"
                        connected={!!me?.hasPersonalGoogleCalendar}
                        selectedCalendarId={me?.personalGoogleCalendarId}
                        onRefreshNeeded={() => qc.refetchQueries({ queryKey: ['me'] })}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                          Sincronizza ora eventi, collaborazioni e prestazioni sul tuo calendario personale.
                        </span>
                        <Button size="sm" variant="secondary" icon={<RefreshCw size={13} />}
                          loading={personalSyncMutation.isPending}
                          onClick={() => personalSyncMutation.mutate()}>
                          Sincronizza ora
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                        Importa automaticamente i tuoi eventi LensCRM nel tuo Google Calendar.
                      </span>
                      <Button size="sm" variant="secondary"
                        icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>}
                        onClick={async () => {
                          const res = await apiGet<{ url: string }>('/calendar/personal/auth-url');
                          window.location.href = res.url;
                        }}>
                        Connetti
                      </Button>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </Section>
        </>
      )}

      {/* Tab: Documenti Legali (ADMIN only) */}
      {activeTab === 'documents' && isAdmin && (
        <Section title="Documenti Legali">
          <LegalDocumentsSection />
        </Section>
      )}

      {/* Tab: Sistema (ADMIN only) */}
      {activeTab === 'system' && isAdmin && (
        <>
          <SmtpConfigSection />
          <GoogleConfigSection />
          <CalendarMaintenanceSection />
          <GcalSyncLogSection />
          <ImportExportSection />
          <BackupSection />
          <SystemHealthSection />
          <AdminActionsSection />
          <InfoAppSection user={me} />
        </>
      )}
    </div>
  );
}
